import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {  getLocalTimeZone } from '@internationalized/date';
import {
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputLabel,
  Box,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";
import { format } from "date-fns";
import CloseIcon from '@mui/icons-material/Close';
import { useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from "react-router-dom";
import {
  searchQuoteAction,
} from "../../redux/actions/quotelistingActions";
import { getGeneralPartyInfoByIdDashboard, clearGpId, updateGeneralPartyInfo }
from "../../redux/actions/generalPartyInfoActions";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DatePickerField from '../../common/DatePickerDashboard';
import dayjs from "dayjs";
import Loader from "../loader/loader";
import DatePickerBot from "../bot/DatePickerBot";
import { CustomTableCell } from "../../common/CustomTableCell";
import { formatPhoneNumber } from "../../common/helpers/commonFun";
import NoDataFound from "../../common/NoDataFound";
import NoSearchResults from "../../common/helpers/NoSearchResults";

const tableHeaders = [
  { id: "quotenumber", label: "Quote Number" },
  { id: "policynumber", label: "Policy Number" },
  { id: "name", label: "Name" },
  { id: "dateofbirth", label: "Date Of Birth" },
  { id: "phonenumber", label: "Phone Number" },
  { id: "createdby", label: "Created By" },
  { id: "createdDate", label: "Created Date" },
  { id: "source", label: "Source" },
  { id: "licensenumber", label: "License Number" },
  { id: "effdate", label: "Effective Date" },
  { id: "expdate", label: "Expiry Date" },
];

const customParseDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return {
    calendar: 'gregory',
    era: 'AD',
    year,
    month,
    day,
    toString() {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    },
    toDate(timeZone) {
      return new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`);
    },
    compare(other) {
      const thisDate = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
      const otherDate = new Date(`${other.year}-${String(other.month).padStart(2, '0')}-${String(other.day).padStart(2, '0')}`);
      return thisDate - otherDate;
    }
  };
};

const QuoteListing = () => {
  const searchQuotes = useSelector((state) => state.quotelisting.searchQuotes);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const navigate = useNavigate();

  const currentDate = dayjs().format("YYYY-MM-DD");
  // const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

  // State variables
  const [searchInput, setSearchInput] = useState("");
  const [quoteData, setQuoteData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
    // const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOption, setSelectedOption] = useState("today");
  const [customDate, setCustomDate] = useState(currentDate);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [generalData, setGeneralData] = useState(null);
   const [orderBy] = useState('quoteNumber');
  const [order] = useState('desc');

  const getToday = () => {
    const today = new Date();
    return customParseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    );
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return customParseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
    );
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [searchError, setSearchError] = useState("");

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const formatDateForSafari = (date) => {
    if (!date) return null;
    return new Date(date.replace(/-/g, "/"));
  };

  const formatDate = (date) => {
    if (!date) return "";
    try {
      const safeDate = formatDateForSafari(date);
      return format(safeDate, "MM-dd-yyyy");
    } catch (error) {
      console.warn("Date formatting error:", error);
      return "";
    }
  };

  // Helper function to parse date consistently
  const parseDate = (dateString) => {
    if (!dateString) return null;

    // Handle different date formats
    let parsedDate;

    // Try parsing as MM-DD-YYYY first
    if (dateString.includes("-") && dateString.split("-").length === 3) {
      const parts = dateString.split("-");
      if (parts[0].length === 2) {
        // MM-DD-YYYY format
        parsedDate = dayjs(`${parts[2]}-${parts[0]}-${parts[1]}`);
      } else {
        // YYYY-MM-DD format
        parsedDate = dayjs(dateString);
      }
    } else {
      // Try direct parsing
      parsedDate = dayjs(dateString);
    }

    return parsedDate.isValid() ? parsedDate : null;
  };

  // Handle dialog option change
  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  // Handle dialog close (keep existing date)
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle proceeding with existing date
  const handleProceedWithExistingDate = () => {
    setOpenDialog(false);
    // Proceed with navigation using the original quote and generalData
    proceedToNavigation(selectedQuote, generalData);
  };

  // Handle setting effective date
  const handleSetEffectiveDate = () => {
    let newEffDate;
    if (selectedOption === "today") {
      newEffDate = dayjs().format("MM-DD-YYYY");
    } else if (selectedOption === "tomorrow") {
      newEffDate = dayjs().add(1, "day").format("MM-DD-YYYY");
    } else {
      newEffDate = dayjs(customDate).format("MM-DD-YYYY");
    }

    // Prepare formData for updateGeneralPartyInfo
    const formData = {
      // gpId: generalData?.gpId,
      ...generalData,
      effectiveDate: newEffDate,
    };

    // Dispatch updateGeneralPartyInfo action
    dispatch(
      updateGeneralPartyInfo(formData, () => {
        // Update generalData with new effectiveDate
        const updatedGeneralData = { ...generalData, effectiveDate: newEffDate };
        // Update quoteData with new effDate
        const updatedQuote = { ...selectedQuote, effDate: newEffDate };
        setOpenDialog(false);
        // Proceed with navigation using updated data
        proceedToNavigation(updatedQuote, updatedGeneralData);
      })
    );
  };

  // Function to proceed with navigation
  const proceedToNavigation = (quote, generalData) => {
    if (!quote?.quoteNumber || !generalData) {
      console.error("quoteNumber or generalData is undefined or missing.");
      return;
    }

    navigate("/mainview", { state: { generalData, updatedQuote: quote } });
  };

  // Handle row click - FIXED VERSION
  const handleRowClickQuotes = async (row) => {
    dispatch(clearGpId())
    const today = dayjs().startOf("day");
    const effDate = parseDate(row.effDate);

    // Fetch generalData first
    const numericPart = row.quoteNumber.replace(/^Q0*/, "");
    try {
      const response = await dispatch(getGeneralPartyInfoByIdDashboard(numericPart));
      const fetchedGeneralData = response?.payload || response;

      if (fetchedGeneralData && Object.keys(fetchedGeneralData).length > 0) {
        setGeneralData(fetchedGeneralData);
        setSelectedQuote(row);

        // Check if effective date is in the past
        if (effDate && effDate.isValid() && effDate.isBefore(today)) {
          setOpenDialog(true); // Open the dialog
        } else {
          // If effective date is not in the past, proceed directly to navigation
          proceedToNavigation(row, fetchedGeneralData);
        }
      } else {
        console.error("No general data returned.");
      }
    } catch (error) {
      console.error("Error fetching general party info:", error);
    }
  };

  // Update quote data when search results change
  useEffect(() => {
    if (hasSearched && searchQuotes) {
      const data = searchQuotes?.content || [];
      const count = searchQuotes?.totalElements || 0;

      setQuoteData(data);
      setTotalCount(count);
    }
  }, [searchQuotes, hasSearched]);

  // Handle search button click
  const handleSearch = () => {
    // Clear previous search error
    setSearchError("");
    
    // Validation: At least one search criteria must be provided
    const hasDateRange = formData.fromDate && formData.toDate;
    const hasSearchTerm = searchInput.trim();

    if (!hasDateRange && !hasSearchTerm) {
      setSearchError("Please provide either a date range or search term to perform the search.");
      return;
    }

    setLoading(true);
    // setIsInitialLoad(false);
    setPage(0);

    const searchParams = {
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format("MM-DD-YYYY") : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format("MM-DD-YYYY") : null,
      search: searchInput.trim() || "",
    };

    dispatch(
      searchQuoteAction(
        searchParams,
        () => {
          setLoading(false);
          setHasSearched(true);
        },
        0,
        rowsPerPage,
        orderBy,
        order

      )
    );
  };

  // Reset search and filters
  const resetSearch = () => {
    setHasSearched(false);
    setSearchInput("");
    setSearchError("");
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setPage(0);
    setFieldErrors({});
    setTouchedFields({});
    setQuoteData([]);
    setTotalCount(0);
    // setIsInitialLoad(true);

    // Clear search results from Redux store
    dispatch({
      type: "SEARCH_QUOTE_SUCCESS",
      payload: { content: [], totalElements: 0 },
    });
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    if (!hasSearched) return;

    setPage(newPage - 1);
    setLoading(true);

    const searchParams = {
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format("MM-DD-YYYY") : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format("MM-DD-YYYY") : null,
      search: searchInput.trim() || "",
    };

    dispatch(
      searchQuoteAction(
        searchParams,
        () => {
          setLoading(false);
        },
        newPage - 1,
        rowsPerPage,
         orderBy,
        order
      )
    );
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    if (!hasSearched) return;

    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLoading(true);

    const searchParams = {
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format("MM-DD-YYYY") : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format("MM-DD-YYYY") : null,
      search: searchInput.trim() || "",
    };

    dispatch(
      searchQuoteAction(
        searchParams,
        () => {
          setLoading(false);
        },
        0,
        newRowsPerPage,
         orderBy,
        order
      )
    );
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={tableHeaders.length} align="center">
            <Loader />
          </TableCell>
        </TableRow>
      );
    }

    if (hasSearched && quoteData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={tableHeaders.length} align="center">
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                <NoSearchResults/>
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return quoteData.map((row) => (
      <TableRow
        className="styled-table-row"
        key={row.quoteNumber}
        onClick={() => handleRowClickQuotes(row)}
        style={{ cursor: "pointer" }}
      >
        <TableCell>{row.quoteNumber}</TableCell>
        <TableCell>{row.policyNumber}</TableCell>
        <TableCell>
          {row.name === "null null" || row.name === null ? "" : row.name}
        </TableCell>
        <TableCell>{formatDate(row.dateOfBirth)}</TableCell>
        <TableCell>{formatPhoneNumber(row.phoneNumber)}</TableCell>
        <TableCell>{row.createdBy?.name || "-"}</TableCell>
        {/* <TableCell>
          {row.createdBy?.timestamp
            ? dayjs(row.createdBy.timestamp).format("MM-DD-YYYY")
            : "-"}
        </TableCell> */}
        <CustomTableCell timestamp={row.createdBy?.timestamp} />
        <TableCell>{row.source}</TableCell>
        <TableCell>{row.licenseNumber}</TableCell>
        <TableCell>{formatDate(row.effDate)}</TableCell>
        <TableCell>{formatDate(row.expDate)}</TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ padding: isMobile ? "0 16px" : "0 50px", overflowX: 'auto' }}>
      <Paper
        elevation={3}
        sx={{
          boxShadow: "none",
          marginTop: "25px",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12}>
              <Typography
                variant="h2"
                sx={{
                  mb: 1,
                }}
              >
                Search Quotes
              </Typography>
            </Grid>
         <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
    <Grid container spacing={3} alignItems="flex-start">


  <Grid item xs={12} sm={6} md={2} sx={{ minHeight: '85px' }}>
    <InputLabel sx={{ color: "black"}}>
      From Date
    </InputLabel>
    <DatePickerField
      fieldName="fromDate"
      formData={formData}
      handleDateChange={handleDateChange}
      handleBlur={handleBlur}
      touchedFields={touchedFields}
      fieldErrors={fieldErrors}
      size={isMobile ? "small" : "medium"}
      sx={{
        width: '100%',
        '& .MuiInputBase-root': {
          height: '55px'
        }
      }}
    />
  </Grid>

  <Grid item xs={12} sm={6} md={2} sx={{ minHeight: '85px' }}>
    <InputLabel sx={{ color: "black"}}>
      To Date
    </InputLabel>
    <DatePickerField
      fieldName="toDate"
      formData={formData}
      handleDateChange={handleDateChange}
      handleBlur={handleBlur}
      touchedFields={touchedFields}
      fieldErrors={fieldErrors}
      size={isMobile ? "small" : "medium"}
    />
  </Grid>

  <Grid item xs={12} sm={8} md={2}>
    <InputLabel sx={{ color: "black"}}>
      Search Term
    </InputLabel>
    <TextField
      fullWidth
      size={isMobile ? "small" : "medium"}
      variant="outlined"
      placeholder="Enter search term"
      value={searchInput}
      onChange={(e) => {
        setSearchInput(e.target.value);
        if (searchError) setSearchError("");
      }}
      error={!!searchError}
      helperText={searchError}
    />
  </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              sx={{
                display: "flex",
                gap: 2,
                minHeight: '85px',
                alignItems: 'flex-end',
              mt:1
              }}
            >
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{marginTop: '15px'}}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={resetSearch}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
          </Box>
        </LocalizationProvider>
      </Paper>


      {!hasSearched ? (

          <NoDataFound discription={"Please use the search filters above to fetch and display updated Quote data."} />
        ) : (
           <>
           <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3, mb: 2 }}>
        Quotes List
      </Typography>
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'auto'  }}>
        <Table sx={{ minWidth: 650 }} aria-label="customized table" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell
                  className="styled-table-heading"
                  key={header.id}
                  align={header.align || "left"}
                  sx={{ fontWeight: "bold", whiteSpace: 'nowrap' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}

      {/* Effective Date Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        disableScrollLock={true}
        keepMounted={false}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}       
      >
        <DialogTitle>
          Confirm Policy Effective Date
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#9ca3af',
              '&:hover': {
                color: '#000',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, mt:2,}}>
            The Effective Date of this quote is in the past. Some carriers may not rate.
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedOption}
              onChange={handleOptionChange}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <FormControlLabel
                value="today"
                control={
                  <Radio
                    sx={{
                      color: '#4b5e6d',
                      
                    }}
                  />
                }
                label="Set to Today"
                sx={{
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#1a1a1a',
                  },
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              />

              <FormControlLabel
                value="setTo"
                control={
                  <Radio />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span>Set to</span>
                    <Box sx={{ flex: 1, minWidth: '150px' }}>
                      <DatePickerBot
                        entry={{
                          id: 'dialogDate',
                          startDate: customDate,
                          isPrefilled: false,
                          isEditing: true,
                        }}
                        handleInputChange={(id, field, value) => {
                          if (id === 'dialogDate' && field === 'startDate') {
                            setCustomDate(value);
                          }
                        }}
                      />
                    </Box>
                  </Box>
                }
                sx={{
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    width: '100%'
                  },
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            onClick={handleSetEffectiveDate}
          >
            Set Effective Date
          </Button>
          <Button
            variant="outlined"
            onClick={handleProceedWithExistingDate}
          >
            Keep Existing Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination - Only show when there are search results */}
      {hasSearched && totalCount > 0 && (
        <Box
          display="flex"
         justifyContent="flex-end"
          alignItems="center"
          padding={2}
          sx={{ fontFamily: '"Poppins", sans-serif', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0, marginBottom: '150px' }}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="body2" sx={{ mr: 1 }}>
              Rows per page:
            </Typography>
            <TextField
              select
              size="small"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 80 }}
            >
              {[5, 10, 15, 25].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
            sx={{ mt: isMobile ? 2 : 0 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuoteListing;